import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import type { ModelLead } from '@app/api/Leads/types';
import { Avatar } from '@app/components/Avatar';
import { SALES_FUNNELS_LEAD_CARD_PATH } from '@app/constants';
import { getLeadsRegistryLeadInfo } from '@app/pages/Leads/LeadsRegistryPage/model/adapters/getLeadsRegistryLeadInfo';
import { formatLeadRegistryTaskPeriod } from '@app/pages/Leads/LeadsRegistryPage/utils';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';
import { Tag } from '@app/shared/ui/Tag';

import type { CSSProperties, FC, ReactNode, Ref } from 'react';

import './LeadsFunnelLeadCard.scss';

interface LeadsFunnelLeadCardProps {
  lead: ModelLead;
  canShowContacts: boolean;
  funnelId: number | null;
  stageId: number | null;
  dragHandle?: ReactNode;
  rootRef?: Ref<HTMLAnchorElement>;
  rootStyle?: CSSProperties;
  isDragging?: boolean;
  isOverlay?: boolean;
}

const MAX_VISIBLE_TAGS = 3;

const LeadsFunnelLeadCardComponent: FC<LeadsFunnelLeadCardProps> = ({
  lead,
  canShowContacts,
  funnelId,
  stageId,
  dragHandle,
  rootRef,
  rootStyle,
  isDragging,
  isOverlay,
}) => {
  const leadInfo = getLeadsRegistryLeadInfo(lead, canShowContacts);
  const visibleTags = (lead.ClientTags ?? [])
    .filter((tag) => !tag.IsDeleted)
    .slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagsCount = Math.max(
    0,
    (lead.ClientTags ?? []).filter((tag) => !tag.IsDeleted).length -
      visibleTags.length
  );
  const currentTaskPeriod = lead.CurrentCrmTask
    ? formatLeadRegistryTaskPeriod(
        lead.CurrentCrmTask.TimeStart,
        lead.CurrentCrmTask.TimeDeadline
      )
    : null;

  return (
    <Link
      ref={rootRef}
      style={rootStyle}
      className={cn('LeadsFunnelLeadCard', {
        'LeadsFunnelLeadCard--draggable': Boolean(dragHandle),
        'LeadsFunnelLeadCard--placeholder': isDragging,
        'LeadsFunnelLeadCard--overlay': isOverlay,
      })}
      to={`${SALES_FUNNELS_LEAD_CARD_PATH}?leadId=${
        lead.Id
      }&source=funnels&funnelId=${funnelId ?? ''}&stageId=${stageId ?? ''}`}
      draggable={false}
      onClick={isOverlay ? (event) => event.preventDefault() : undefined}
    >
      {dragHandle}

      <div className="LeadsFunnelLeadCard__header">
        <Avatar
          className="LeadsFunnelLeadCard__avatar"
          src={leadInfo.avatarUrl}
          nameFirst={leadInfo.clientName}
          size={28}
          round
        />

        <div className="LeadsFunnelLeadCard__identity">
          <span className="LeadsFunnelLeadCard__name">
            {leadInfo.clientName}
          </span>
          <span className="LeadsFunnelLeadCard__phone">
            {leadInfo.clientPhone ?? ''}
          </span>
        </div>

        {leadInfo.lastMessageTime && (
          <span className="LeadsFunnelLeadCard__time">
            {leadInfo.lastMessageTime}
          </span>
        )}
      </div>

      <div className="LeadsFunnelLeadCard__message">
        <SocialIntegrationIcon
          type={leadInfo.socialIntegrationType}
          size="sm"
          className="LeadsFunnelLeadCard__integration"
        />
        <span className="LeadsFunnelLeadCard__message-text">
          {leadInfo.lastMessage}
        </span>
      </div>

      {(lead.UserName || currentTaskPeriod) && (
        <div className="LeadsFunnelLeadCard__meta">
          {lead.UserName && (
            <span className="LeadsFunnelLeadCard__meta-item">
              <UserOutlined />
              {lead.UserName}
            </span>
          )}
          {currentTaskPeriod && (
            <span className="LeadsFunnelLeadCard__meta-item">
              <ClockCircleOutlined />
              {currentTaskPeriod}
            </span>
          )}
        </div>
      )}

      {visibleTags.length > 0 && (
        <div className="LeadsFunnelLeadCard__tags">
          {visibleTags.map((tag) => (
            <Tag
              key={tag.Id}
              className="LeadsFunnelLeadCard__tag"
              item={{
                id: tag.Id,
                label: tag.Name ?? '',
                color: tag.Color ?? undefined,
                iconId: tag.IconId ?? undefined,
              }}
            />
          ))}
          {hiddenTagsCount > 0 && (
            <span className="LeadsFunnelLeadCard__more-tags">
              +{hiddenTagsCount}
            </span>
          )}
        </div>
      )}
    </Link>
  );
};

export const LeadsFunnelLeadCard = memo(LeadsFunnelLeadCardComponent);
